// BLENDER_PATHWAY_ROUTE_AUTHORITY_V1_8_3B
import React, { useMemo } from "react";
import PathwayFogTeaser from "./PathwayFogTeaser";
import ConfigurationLandscapeCanvas from "./ConfigurationLandscapeCanvas";
import BlenderTerrainUnderlay from "./BlenderTerrainUnderlay";
import BlenderPathwayRoutes from "./BlenderPathwayRoutes";

const TAU = Math.PI * 2;

function polar(cx, cy, radius, angle) {
  return {
    x: cx + Math.cos(angle) * radius,
    y: cy + Math.sin(angle) * radius,
  };
}

function shortLabel(value = "", max = 30) {
  const clean = String(value || "").trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function hash(value = "") {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h >>> 0);
}

function landscapeNodes(groups) {
  const visible = groups.slice(0, 8);
  const count = Math.max(visible.length, 1);

  const cx = 500;
  const cy = 395;

  return visible.map((group, index) => {
    const angle = -Math.PI / 2 + (TAU / count) * index;
    const seed = hash(group.key || group.label || String(index));

    /*
     * Intentionally asymmetric radii.
     * The reference works because it feels like a field, not a perfect wheel.
     */
    const radius =
      245 +
      ((seed % 59) - 29) +
      (index % 3 === 0 ? 22 : 0);

    const p = polar(cx, cy, radius, angle);

    return {
      ...group,
      x: p.x,
      y: p.y,
      index,
      angle,
      seed,
      form: seed % 3,
    };
  });
}

function routePath(node) {
  const cx = 500;
  const cy = 395;

  const dx = node.x - cx;
  const dy = node.y - cy;
  const bend = node.index % 2 === 0 ? 0.13 : -0.13;

  const controlX = cx + dx * 0.47 - dy * bend;
  const controlY = cy + dy * 0.47 + dx * bend;

  return `M ${cx} ${cy} Q ${controlX} ${controlY} ${node.x} ${node.y}`;
}

function LandscapePatch({ node, focused }) {
  const x = node.x;
  const y = node.y;
  const orientation = node.orientation || "unresolved";
  const form = node.form;

  if (orientation === "constrains") {
    return (
      <g className={`rr-canvas-patch constrains ${focused ? "focused" : ""}`}>
        <path
          d={`M ${x - 74} ${y + 42}
              C ${x - 57} ${y + 3}, ${x - 29} ${y - 56}, ${x - 2} ${y - 18}
              C ${x + 27} ${y - 53}, ${x + 59} ${y + 2}, ${x + 76} ${y + 41}
              C ${x + 42} ${y + 67}, ${x - 42} ${y + 65}, ${x - 74} ${y + 42} Z`}
          className="rr-canvas-pressure"
        />
        <path
          d={`M ${x - 58} ${y + 30}
              L ${x - 31} ${y - 29}
              L ${x - 7} ${y + 9}
              L ${x + 22} ${y - 25}
              L ${x + 58} ${y + 29}`}
          className="rr-canvas-ridge"
        />
      </g>
    );
  }

  if (orientation === "mixed") {
    return (
      <g className={`rr-canvas-patch mixed ${focused ? "focused" : ""}`}>
        <path
          d={`M ${x - 70} ${y + 40}
              C ${x - 54} ${y - 20}, ${x - 13} ${y - 52}, ${x + 4} ${y - 14}
              C ${x + 29} ${y + 12}, ${x + 51} ${y + 4}, ${x + 72} ${y + 39}
              C ${x + 36} ${y + 66}, ${x - 33} ${y + 65}, ${x - 70} ${y + 40} Z`}
          className="rr-canvas-tradeoff"
        />
        <path
          d={`M ${x - 53} ${y + 26}
              Q ${x - 15} ${y - 6} ${x} ${y - 18}
              Q ${x + 24} ${y + 8} ${x + 53} ${y + 31}`}
          className="rr-canvas-fork"
        />
        <path
          d={`M ${x} ${y - 18}
              Q ${x + 2} ${y - 51} ${x + 43} ${y - 61}`}
          className="rr-canvas-fork alt"
        />
      </g>
    );
  }

  if (orientation !== "affords") {
    return (
      <g className={`rr-canvas-patch unresolved ${focused ? "focused" : ""}`}>
        <ellipse cx={x} cy={y} rx="73" ry="48" className="rr-canvas-fog" />
        <text x={x} y={y + 7} textAnchor="middle" className="rr-canvas-fog-mark">
          ?
        </text>
      </g>
    );
  }

  if (form === 0) {
    return (
      <g className={`rr-canvas-patch affords clearing ${focused ? "focused" : ""}`}>
        <ellipse cx={x} cy={y} rx="76" ry="52" className="rr-canvas-clearing" />
        <path
          d={`M ${x - 60} ${y + 27}
              Q ${x - 27} ${y - 18} ${x} ${y - 24}
              Q ${x + 30} ${y - 13} ${x + 61} ${y + 25}`}
          className="rr-canvas-opening-route"
        />
        <circle cx={x - 40} cy={y + 11} r="4" className="rr-canvas-tree" />
        <circle cx={x + 35} cy={y - 2} r="5" className="rr-canvas-tree" />
        <circle cx={x + 8} cy={y + 29} r="3.5" className="rr-canvas-tree" />
      </g>
    );
  }

  if (form === 1) {
    return (
      <g className={`rr-canvas-patch affords grove ${focused ? "focused" : ""}`}>
        <path
          d={`M ${x - 74} ${y + 39}
              C ${x - 63} ${y - 24}, ${x - 35} ${y - 57}, ${x - 5} ${y - 31}
              C ${x + 17} ${y - 62}, ${x + 55} ${y - 27}, ${x + 76} ${y + 35}
              C ${x + 37} ${y + 65}, ${x - 37} ${y + 67}, ${x - 74} ${y + 39} Z`}
          className="rr-canvas-grove"
        />
        <circle cx={x - 33} cy={y + 8} r="8" className="rr-canvas-tree large" />
        <circle cx={x + 4} cy={y - 20} r="7" className="rr-canvas-tree large" />
        <circle cx={x + 38} cy={y + 13} r="7" className="rr-canvas-tree large" />
        <path
          d={`M ${x - 51} ${y + 31} Q ${x} ${y - 7} ${x + 53} ${y + 31}`}
          className="rr-canvas-opening-route"
        />
      </g>
    );
  }

  return (
    <g className={`rr-canvas-patch affords corridor ${focused ? "focused" : ""}`}>
      <path
        d={`M ${x - 76} ${y + 33}
            C ${x - 34} ${y + 7}, ${x - 20} ${y - 41}, ${x} ${y - 48}
            C ${x + 23} ${y - 34}, ${x + 28} ${y + 9}, ${x + 74} ${y + 34}`}
        className="rr-canvas-corridor"
      />
      <path
        d={`M ${x - 68} ${y + 32}
            C ${x - 29} ${y + 5}, ${x - 15} ${y - 27}, ${x} ${y - 33}
            C ${x + 22} ${y - 21}, ${x + 31} ${y + 12}, ${x + 67} ${y + 30}`}
        className="rr-canvas-opening-route"
      />
    </g>
  );
}

function Callout({ node }) {
  const upper = node.y < 395;
  const calloutY = upper ? node.y - 102 : node.y + 89;

  return (
    <g className="rr-canvas-callout">
      <line
        x1={node.x}
        y1={node.y + (upper ? -11 : 11)}
        x2={node.x}
        y2={calloutY + (upper ? 19 : -19)}
        className="rr-canvas-callout-line"
      />
      <text
        x={node.x}
        y={calloutY}
        textAnchor="middle"
        className="rr-canvas-callout-label"
      >
        {shortLabel(node.label)}
      </text>
      <text
        x={node.x}
        y={calloutY + 18}
        textAnchor="middle"
        className="rr-canvas-callout-meta"
      >
        {node.pathwayCount} paths · {node.rootCount} roots
      </text>
    </g>
  );
}

export default function TerrainRadialOverview({
  capabilityLabel,
  groups,
  state,
  focusKey,
  onFocus,
  onBlur,
  onInspect,
}) {
  const nodes = useMemo(() => landscapeNodes(groups), [groups]);
  const hidden = Math.max(0, groups.length - nodes.length);

  const active =
    groups.find((group) => group.key === focusKey) ||
    null;

  const totalPathways = groups.reduce(
    (sum, group) => sum + (Number(group.pathwayCount) || 0),
    0
  );

  const substrate = (
    <>
      <BlenderTerrainUnderlay />

      <defs>
        <radialGradient id="rrCanvasBase">
          <stop offset="0%" stopColor="#f7faf5" />
          <stop offset="50%" stopColor="#eff4ea" />
          <stop offset="100%" stopColor="#e9e4d8" />
        </radialGradient>
        <filter id="rrCanvasMist">
          <feGaussianBlur stdDeviation="18" />
        </filter>
      </defs>

      <rect x="35" y="25" width="930" height="700" rx="46" fill="url(#rrCanvasBase)" />

      <path
        d="M49 520 C187 432 291 560 395 491 S589 312 706 365 S858 538 951 461"
        className="rr-canvas-contour"
      />
      <path
        d="M44 263 C177 161 285 252 398 207 S664 114 949 237"
        className="rr-canvas-contour subtle"
      />
      <path
        d="M78 718 C218 625 332 650 405 553 S524 366 650 273 S809 113 925 46"
        className="rr-canvas-water"
      />

      <path
        d="M105 587 C213 528 296 560 367 510 S505 455 598 486 S767 588 882 547"
        className="rr-canvas-secondary-route"
      />
      <path
        d="M89 340 C212 291 302 330 395 282 S632 199 822 276"
        className="rr-canvas-secondary-route faint"
      />
      <path
        d="M219 129 C313 185 357 166 426 129 S607 102 729 145"
        className="rr-canvas-secondary-route faint"
      />
    </>
  );

  const landscape = (
    <>
      {nodes.map((node) => {
        const focused = node.key === focusKey;

        return (
          <g
            key={`${node.key}-patch`}
            className={`rr-canvas-node-group ${node.orientation || "unresolved"} ${
              focused ? "focused" : ""
            }`}
            role="button"
            tabIndex="0"
            onMouseEnter={() => onFocus?.(node.key)}
            onMouseLeave={() => onBlur?.()}
            onFocus={() => onFocus?.(node.key)}
            onBlur={() => onBlur?.()}
            onClick={() => onInspect?.(node)}
          >
            <circle
              cx={node.x}
              cy={node.y}
              r={82 + Math.min(23, (node.pathwayCount || 1) * 5)}
              className="rr-canvas-haze"
              filter="url(#rrCanvasMist)"
            />

            <LandscapePatch node={node} focused={focused} />

            <circle
              cx={node.x}
              cy={node.y}
              r={focused ? 10 : 7}
              className="rr-canvas-anchor"
            />
          </g>
        );
      })}
    </>
  );

  const routes = (
    <>
      {nodes.map((node) => (
        <path
          key={`${node.key}-route`}
          d={routePath(node)}
          className={`rr-canvas-main-route ${node.orientation || "unresolved"} ${
            node.key === focusKey ? "focused" : ""
          }`}
        />
      ))}
    </>
  );

  const capability = (
    <>
      <circle cx="500" cy="395" r="126" className="rr-canvas-core-halo" />
      <circle cx="500" cy="395" r="95" className="rr-canvas-core" />

      <text x="500" y="342" textAnchor="middle" className="rr-canvas-core-kicker">
        ACTIVE FIELD
      </text>
      <text x="500" y="389" textAnchor="middle" className="rr-canvas-core-title">
        {shortLabel(capabilityLabel, 31)}
      </text>
      <text x="500" y="425" textAnchor="middle" className="rr-canvas-core-meta">
        {state?.independent_root_count ?? "—"} independent roots
      </text>
      <text x="500" y="449" textAnchor="middle" className="rr-canvas-core-meta">
        Configuration × Capability
      </text>
    </>
  );

  const callouts = (
    <>
      {nodes.map((node) => (
        <Callout key={`${node.key}-callout`} node={node} />
      ))}
    </>
  );

  const footer = active ? (
    <PathwayFogTeaser
      group={active}
      compact
      onInspect={onInspect}
    />
  ) : (
    <div className="rr-config-canvas-start">
      <span className="v25a-eyebrow">START HERE</span>
      <p>
        Read one configuration before comparing it. Each labelled landscape
        patch is a teaser for one configured pathway group; nothing is selected.
      </p>
    </div>
  );

  return (
    <section className="rr-radial-overview rr-radial-canvas-v1">
      <header className="rr-radial-copy">
        <div>
          <span className="v25a-eyebrow">UNDERSTAND ONE CONFIGURATION</span>
          <h3>A configured landscape for one capability</h3>
        </div>
        <span className="rr-primary-view-badge">PRIMARY VIEW</span>
      </header>

      <p className="rr-radial-intro">
        The canvas separates substrate, landscape, routes, capability and
        callouts so the field can become richer without changing the analytical
        objects underneath it.
      </p>

      <div className="rr-config-canvas-summary">
        <span><b>{groups.length}</b> pathway groups</span>
        <span><b>{totalPathways}</b> materialized pathways</span>
        <span><b>{state?.independent_root_count ?? "—"}</b> independent roots</span>
        <span className="boundary">visual texture ≠ analytical strength</span>
      </div>

      <ConfigurationLandscapeCanvas
        ariaLabel={`Configured landscape for ${capabilityLabel}`}
        substrate={substrate}
        landscape={landscape}
        routes={<BlenderPathwayRoutes />}
        capability={capability}
        callouts={callouts}
        footer={footer}
      />

      {hidden ? (
        <div className="rr-config-canvas-more">
          +{hidden} pathway groups outside this overview
        </div>
      ) : null}

      <div className="rr-radial-instruction">
        <span>whole configuration</span>
        <i>→</i>
        <span>pathway teaser</span>
        <i>→</i>
        <span>research explanation</span>
        <i>→</i>
        <span>provenance</span>
      </div>
    </section>
  );
}
